import React, { useState, useEffect } from 'react';
import { Upload, Card, Image, Button, Space, message, Modal } from 'antd';
import { PlusOutlined, DeleteOutlined, StarOutlined, StarFilled, DownloadOutlined } from '@ant-design/icons';
import type { UploadFile } from 'antd/es/upload/interface';
import { api, FILE_BASE_URL } from '../services/api';
import { useCanDelete } from '../hooks/useCanDelete';

interface ProductImage {
  id: number;
  originalFileName: string;
  originalFilePath: string;
  thumbnailFilePath: string;
  sortOrder: number;
  isPrimary: boolean;
}

interface ProductImageUploaderProps {
  productId?: number;
  onChange?: (images: ProductImage[]) => void;
}

const ProductImageUploader: React.FC<ProductImageUploaderProps> = ({ productId, onChange }) => {
  const canDelete = useCanDelete();
  const [images, setImages] = useState<ProductImage[]>([]);
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewImage, setPreviewImage] = useState('');
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (productId) {
      loadImages();
    }
  }, [productId]);

  const loadImages = async () => {
    if (!productId) return;

    try {
      const { data } = await api.get<ProductImage[]>(`/productimages/product/${productId}`);
      setImages(data);
      
      // Convert to UploadFile format for display
      const uploadFiles: UploadFile[] = data.map(img => ({
        uid: img.id.toString(),
        name: img.originalFileName,
        status: 'done',
        url: `${FILE_BASE_URL}${img.thumbnailFilePath}`,
        thumbUrl: `${FILE_BASE_URL}${img.thumbnailFilePath}`,
      }));
      setFileList(uploadFiles);
      
      if (onChange) {
        onChange(data);
      }
    } catch (error) {
      message.error('Görseller yüklenirken hata oluştu');
    }
  };

  const handleUpload = async (file: File) => {
    if (!productId) {
      message.warning('Önce ürünü kaydetmelisiniz');
      return false;
    }

    const formData = new FormData();
    formData.append('file', file);

    try {
      setUploading(true);
      const { data } = await api.post<ProductImage>(`/productimages/upload/${productId}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      message.success('Görsel başarıyla yüklendi');
      await loadImages();
      return false; // Prevent default upload behavior
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Görsel yüklenirken hata oluştu');
      return false;
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (imageId: number) => {
    try {
      await api.delete(`/productimages/${imageId}`);
      message.success('Görsel başarıyla silindi');
      await loadImages();
    } catch (error) {
      message.error('Görsel silinirken hata oluştu');
    }
  };

  const handleSetPrimary = async (imageId: number) => {
    try {
      const image = images.find(img => img.id === imageId);
      if (!image) return;

      await api.put(`/productimages/${imageId}`, {
        id: imageId,
        sortOrder: image.sortOrder,
        isPrimary: true
      });
      
      message.success('Ana görsel ayarlandı');
      await loadImages();
    } catch (error) {
      message.error('Ana görsel ayarlanırken hata oluştu');
    }
  };

  const handlePreview = async (file: UploadFile) => {
    setPreviewImage(file.url || file.thumbUrl || '');
    setPreviewVisible(true);
  };

  const handleDownload = async (filePath: string, fileName: string) => {
    try {
      const url = `${FILE_BASE_URL}${filePath}`;
      const response = await fetch(url);
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Clean up the blob URL
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      message.error('Görsel indirilirken hata oluştu');
    }
  };

  const uploadButton = (
    <div>
      <PlusOutlined />
      <div style={{ marginTop: 8 }}>Görsel Yükle</div>
    </div>
  );

  return (
    <Card title="Ürün Görselleri" extra={productId ? null : <span style={{ color: '#ff4d4f', fontSize: 12 }}>Görsel yüklemek için önce ürünü kaydedin</span>}>
      <Space direction="vertical" style={{ width: '100%' }}>
        <Upload
          listType="picture-card"
          fileList={fileList}
          onPreview={handlePreview}
          beforeUpload={handleUpload}
          disabled={!productId || uploading}
          accept="image/*"
          showUploadList={{
            showPreviewIcon: true,
            showRemoveIcon: false,
          }}
        >
          {fileList.length >= 10 ? null : uploadButton}
        </Upload>

        {images.length > 0 && (
          <div>
            <p style={{ marginBottom: 8, fontWeight: 500 }}>Yüklenen Görseller:</p>
            <Space direction="vertical" style={{ width: '100%' }}>
              {images.map(img => (
                <Card key={img.id} size="small">
                  <Space>
                    <Image
                      width={60}
                      height={60}
                      src={`${FILE_BASE_URL}${img.thumbnailFilePath}`}
                      style={{ objectFit: 'cover', borderRadius: 4 }}
                    />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 500 }}>{img.originalFileName}</div>
                      {img.isPrimary && (
                        <span style={{ color: '#faad14', fontSize: 12 }}>
                          <StarFilled /> Ana Görsel
                        </span>
                      )}
                    </div>
                    <Space>
                      {!img.isPrimary && (
                        <Button
                          size="small"
                          icon={<StarOutlined />}
                          onClick={() => handleSetPrimary(img.id)}
                        >
                          Ana Yap
                        </Button>
                      )}
                      <Button
                        size="small"
                        icon={<DownloadOutlined />}
                        onClick={() => handleDownload(img.originalFilePath, img.originalFileName)}
                        title="Orijinal görseli indir"
                      >
                        Orijinal
                      </Button>
                      <Button
                        size="small"
                        icon={<DownloadOutlined />}
                        onClick={() => handleDownload(img.thumbnailFilePath, `thumbnail_${img.originalFileName}`)}
                        title="Thumbnail görseli indir"
                      >
                        Thumbnail
                      </Button>
                      {canDelete && (
                      <Button
                        size="small"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => handleDelete(img.id)}
                      >
                        Sil
                      </Button>
                    )}
                    </Space>
                  </Space>
                </Card>
              ))}
            </Space>
          </div>
        )}
      </Space>

      <Modal
        open={previewVisible}
        footer={null}
        onCancel={() => setPreviewVisible(false)}
      >
        <img alt="preview" style={{ width: '100%' }} src={previewImage} />
      </Modal>
    </Card>
  );
};

export default ProductImageUploader;

